import React, { useState, useEffect } from "react";
import { FiCheck, FiX, FiInfo, FiAlertCircle } from "react-icons/fi";

const ToastContext = React.createContext();

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = "success", duration = 3000) => {
    // Also make it available globally for components that can't use the hook
    window.showToast = showToast;
    const id = Date.now() + Math.random();
    const toast = { id, message, type };
    
    setToasts((prev) => [...prev, toast]);
    
    setTimeout(() => {
      removeToast(id);
    }, duration);
    
    return id;
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      <div
        style={{
          position: "fixed",
          top: "80px",
          right: "20px",
          zIndex: 10000,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          pointerEvents: "none",
        }}
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onClose }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const icons = {
    success: <FiCheck />,
    error: <FiX />,
    info: <FiInfo />,
    warning: <FiAlertCircle />,
  };

  const colors = {
    success: { bg: "rgba(20, 184, 166, 0.15)", border: "rgba(20, 184, 166, 0.5)", icon: "var(--color-primary)" },
    error: { bg: "rgba(239, 68, 68, 0.15)", border: "rgba(239, 68, 68, 0.5)", icon: "#ef4444" },
    info: { bg: "rgba(59, 130, 246, 0.15)", border: "rgba(59, 130, 246, 0.5)", icon: "var(--color-accent)" },
    warning: { bg: "rgba(249, 115, 22, 0.15)", border: "rgba(249, 115, 22, 0.5)", icon: "var(--color-secondary)" },
  };

  const style = colors[toast.type] || colors.success;

  return (
    <div
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderRadius: "12px",
        padding: "12px 16px",
        minWidth: "280px",
        maxWidth: "400px",
        color: "white",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(10px)",
        transform: isVisible ? "translateX(0)" : "translateX(400px)",
        opacity: isVisible ? 1 : 0,
        transition: "all 0.3s ease",
        pointerEvents: "auto",
      }}
    >
      <div style={{ fontSize: "1.2em", color: style.icon, flexShrink: 0 }}>
        {icons[toast.type]}
      </div>
      <div style={{ flex: 1, fontSize: "0.95em" }}>{toast.message}</div>
      <button
        onClick={onClose}
        style={{
          background: "transparent",
          border: "none",
          color: "white",
          cursor: "pointer",
          padding: "4px",
          display: "flex",
          alignItems: "center",
          opacity: 0.7,
          transition: "opacity 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.7)}
        aria-label="Close notification"
      >
        <FiX />
      </button>
    </div>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}

